import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Checkbox, Field, Form, Input, Select, TextArea, Typography } from '@/components';

import { OtherInfo, Project } from '@/features/projects/types';

import {
  formatStringAsInteger,
  nullableStringSetter,
  parseStringToInteger,
  getDateFromQuarterAndYear,
  getQuarterFromDate,
  getYearFromDate,
} from '@/helpers';

export type OtherInfoFormFields = {
  type_of_revenue: string;
  energy_offset: string | null;
  energy_offset_details: string | null;
  financial_details: string | null;
  other_comments: string | null;
};

export type OtherInfoFormPayload = {
  other_info: OtherInfoFormFields[];
};

export type OtherInfoFormProps = {
  readonly id: string;
  readonly project: Project;
  readonly onSubmit: (data: OtherInfoFormPayload) => void;
};

export function OtherInfoFormNew({ id, project, onSubmit }: OtherInfoFormProps) {
  const { t } = useTranslation(['projects']);

  const item = project.other_info?.[0] ?? ({} as Partial<OtherInfo>);

  const typeOfRevenue = [
    { label: 'Merchant', value: 'merchant' },
    { label: 'Contracted', value: 'contracted' },
  ];

  const options = useMemo(
    () => ({
      defaultValues: {
        type_of_revenue: item.type_of_revenue ?? null,
        energy_offset: item.energy_offset ?? '',
        energy_offset_details: item.energy_offset_details ?? null,
        financial_details: item.financial_details ?? null,
        other_comments: item.other_comments ?? null,
      },
    }),
    [project]
  );

  return (
    <Form<OtherInfoFormFields> id={id} onSubmit={(data) => onSubmit({ other_info: [data] })} options={options}>
      {({ formState, register }) => (
        <div className="space-y-6">
          <Typography as="p" color="secondary" size="body-3" weight="normal" className="mt-1 italic">
            {t("Please provide information about the project's and any relevant comments.")}
          </Typography>

          <Field
            id="type_of_revenue"
            label={t('Type of Revenue')}
            error={formState.errors['type_of_revenue']}
            delay={0}
          >
            <Select
              id="type_of_revenue"
              registration={register('type_of_revenue', { setValueAs: nullableStringSetter })}
              options={typeOfRevenue}
              placeholder={t('Select type of revenue')}
              color={formState.errors['type_of_revenue'] ? 'error' : 'primary'}
            />
          </Field>

          <Field
            id="energy_offset"
            label={t('Energy offset')}
            error={formState.errors['energy_offset']}
            delay={0}
            classes={{ field: 'space-y-1' }}
          >
            <Input
              id="energy_offset"
              type="text"
              placeholder={t('e.g. Feed in-Tariff')}
              color={formState.errors['energy_offset'] ? 'error' : 'primary'}
              registration={register('energy_offset')}
            />
          </Field>

          <Field
            id="energy_offset_details"
            label={t('Energy offset details')}
            error={formState.errors['energy_offset_details']}
            delay={0.15}
            classes={{ field: 'space-y-1' }}
          >
            <TextArea
              id="energy_offset_details"
              placeholder={t('Type here...')}
              color="primary"
              registration={register('energy_offset_details', { setValueAs: nullableStringSetter })}
            />
          </Field>

          <Field
            id="financial_details"
            label={t('Financial details')}
            error={formState.errors['financial_details']}
            delay={0.15}
            classes={{ field: 'space-y-1' }}
          >
            <TextArea
              id="financial_details"
              placeholder={t('Type here...')}
              color="primary"
              registration={register('financial_details', { setValueAs: nullableStringSetter })}
            />
          </Field>

          <Field
            id="other_comments"
            label={t('Other comments')}
            error={formState.errors['other_comments']}
            delay={0.15}
            classes={{ field: 'space-y-1' }}
          >
            <TextArea
              id="other_comments"
              placeholder={t('Type here...')}
              color="primary"
              registration={register('other_comments', { setValueAs: nullableStringSetter })}
            />
          </Field>
        </div>
      )}
    </Form>
  );
}
